import React from 'react';
import { SanteFinanciere } from '../types';
import {
  CurrencyEuroIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

interface DashboardSanteFinanciereProps {
  data: SanteFinanciere;
  onOuvrirProjet: (projetId: string) => void;
  onOuvrirGraphique?: (type: 'marge' | 'sante') => void;
}

const fmt = (n: number) => Math.abs(Math.round(n)).toLocaleString('fr-FR');

const DashboardSanteFinanciere: React.FC<DashboardSanteFinanciereProps> = ({
  data,
  onOuvrirProjet,
  onOuvrirGraphique,
}) => {
  const isEconomie = data.projectionGlobale <= 0;
  const isMargePositive = data.margeGlobalePortfolio >= 0;
  const isMATPositif = data.matGlobal >= 0;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
          <CurrencyEuroIcon className="w-4 h-4 text-violet-600" />
        </div>
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
          Santé Financière
        </h3>
        <span className="text-[11px] text-gray-400 ml-auto">Vision moyen terme</span>
      </div>

      {/* Marge globale + MAT + Projection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div
          onClick={() => onOuvrirGraphique?.('marge')}
          className={`rounded-lg p-4 border cursor-pointer transition-all hover:shadow-md ${
            isMargePositive ? 'bg-emerald-50 border-emerald-100 hover:border-emerald-300' : 'bg-red-50 border-red-100 hover:border-red-300'
          }`}
        >
          <p className={`text-[11px] font-medium uppercase tracking-wide mb-1.5 ${isMargePositive ? 'text-emerald-400' : 'text-red-400'}`}>
            Marge globale portfolio
          </p>
          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-bold ${isMargePositive ? 'text-emerald-600' : 'text-red-500'}`}>
              {isMargePositive ? '+' : '-'}{fmt(data.margeGlobalePortfolio)} €
            </span>
          </div>
          <span className={`text-xs font-medium ${isMargePositive ? 'text-emerald-500' : 'text-red-400'}`}>
            Taux : {data.tauxMargeGlobalPortfolio.toFixed(1)}%
          </span>
        </div>

        <div
          onClick={() => onOuvrirGraphique?.('sante')}
          className={`rounded-lg p-4 border cursor-pointer transition-all hover:shadow-md ${
            isMATPositif ? 'bg-blue-50 border-blue-100 hover:border-blue-300' : 'bg-red-50 border-red-100 hover:border-red-300'
          }`}
        >
          <p className={`text-[11px] font-medium uppercase tracking-wide mb-1.5 ${isMATPositif ? 'text-blue-400' : 'text-red-400'}`}>
            MAT Portfolio
          </p>
          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-bold ${isMATPositif ? 'text-blue-600' : 'text-red-500'}`}>
              {isMATPositif ? '+' : '-'}{fmt(data.matGlobal)} €
            </span>
          </div>
          <span className={`text-xs font-medium ${isMATPositif ? 'text-blue-500' : 'text-red-400'}`}>
            Taux MAT : {data.tauxMATGlobal.toFixed(1)}%
          </span>
        </div>

        <div className={`rounded-lg p-4 border ${isEconomie ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
          <p className={`text-[11px] font-medium uppercase tracking-wide mb-1.5 ${isEconomie ? 'text-emerald-400' : 'text-red-400'}`}>
            Projection financière
          </p>
          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-bold ${isEconomie ? 'text-emerald-600' : 'text-red-500'}`}>
              {data.projectionGlobale > 0 ? '+' : ''}{fmt(data.projectionGlobale)} €
            </span>
          </div>
          <span className={`text-xs font-medium ${isEconomie ? 'text-emerald-500' : 'text-red-400'}`}>
            {isEconomie ? "dans le budget" : 'de dépassement (TS)'}
          </span>
        </div>
      </div>

      {/* Dérive totale */}
      {data.totalDerive > 0 && (
        <div className="flex items-center justify-between py-2 px-3 bg-amber-50 rounded-lg border border-amber-100 mb-4">
          <div className="flex items-center gap-2">
            <ArrowTrendingDownIcon className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-medium text-gray-600">Dérive totale détectée</span>
          </div>
          <span className="text-sm font-bold text-amber-600">{fmt(data.totalDerive)} €</span>
        </div>
      )}

      {/* Projets à surveiller */}
      {data.projetsASurveiller.length > 0 ? (
        <div>
          <div className="flex items-center gap-1.5 mb-3">
            <EyeIcon className="w-4 h-4 text-violet-500" />
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              Projets à surveiller
            </p>
            <span className="text-[10px] text-gray-400 ml-auto">
              {data.projetsASurveiller.length} projet{data.projetsASurveiller.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-1.5">
            {data.projetsASurveiller.map((p) => (
              <div
                key={p.projetId}
                onClick={() => onOuvrirProjet(p.projetId)}
                className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-violet-50 cursor-pointer transition-colors group"
              >
                <ExclamationTriangleIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  p.urgence === 'haute' ? 'text-red-500' : 'text-amber-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-medium text-gray-700 truncate">{p.projetNom}</p>
                    <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                      p.urgence === 'haute' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
                    }`}>
                      {p.urgence === 'haute' ? 'Urgent' : 'Vigilance'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                    {p.raisons.map((raison, i) => (
                      <span key={i} className="text-[11px] text-gray-500">
                        {i > 0 && <span className="text-gray-300 mr-2">|</span>}
                        {raison}
                      </span>
                    ))}
                  </div>
                </div>
                <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-violet-500 flex-shrink-0 mt-1" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-sm text-emerald-600 font-medium">Aucun projet à surveiller</p>
          <p className="text-xs text-gray-400 mt-1">Tous les projets sont dans les clous</p>
        </div>
      )}
    </div>
  );
};

export default DashboardSanteFinanciere;
